import { formatCurrency } from 'v2/shared/utils'
import type { RCard } from 'v2/types/api/billing'
import { CardSelector } from '../components/card-selector'
import { type RazorpayMethod, RazorpayMethodPicker } from '../components/razorpay-method-picker'
import { RechargeAmountTile } from '../components/recharge-amount-tile'

// Step 1 of the Razorpay Custom Checkout modal: choose an amount, choose a
// payment mode, and either pay with a saved card or continue to the card-detail
// step. Extracted so the modal keeps only the flow/payment logic (repo
// convention: split components > 150 lines) and so step 2 can render the card
// fields ALONE — the amount belongs to this step, and showing the chips again
// next to the card inputs made the second screen look like a fresh, unrelated
// payment.

export interface RazorpayMethodPickerOptions {
  onSelectMethod: (method: RazorpayMethod) => void
  showNetbanking: boolean
  showBankTransfer: boolean
  netbankingDisabledReason?: string
  bankTransferDisabledReason?: string
}

interface RazorpayCustomAmountStepProps {
  currency: string
  choices: number[]
  selectedAmount: number
  onSelectAmount: (amount: number) => void
  isPending: boolean
  isAddCardIntent: boolean
  gatewayFeeRate?: number
  cardFeeLabel?: string
  showSavedCards: boolean
  activeCards: RCard[]
  selectedCardId: number
  onCardChange: (cardId: number) => void
  onAddCard: () => void
  // The selected method is always known — an entry point that offers no choice
  // still pins one, and the fee breakup below depends on it.
  method: RazorpayMethod
  // The picker's wiring, and its presence IS the decision to render it. This
  // used to be a `showMethodPicker` boolean beside the five values the picker
  // needs, which let the two disagree: a caller could ask for a picker and pass
  // nothing to put in it, or supply the wiring and have it silently ignored.
  // Hidden when only one method is on offer (or the entry point already pinned
  // one), exactly as the Standard modal does.
  methodPicker?: RazorpayMethodPickerOptions
}

export const RazorpayCustomAmountStep = ({
  currency,
  choices,
  selectedAmount,
  onSelectAmount,
  isPending,
  isAddCardIntent,
  gatewayFeeRate,
  cardFeeLabel,
  showSavedCards,
  activeCards,
  selectedCardId,
  onCardChange,
  onAddCard,
  method,
  methodPicker,
}: RazorpayCustomAmountStepProps) => {
  const isCard = method === 'card'
  const showMethodPicker = !!methodPicker
  // The gateway fee is a CARD fee — netbanking and bank transfer are fee-free and
  // the order is priced accordingly, so the breakup must not follow them.
  const showCardFeeBreakup = isCard && !!gatewayFeeRate && selectedAmount > 0
  const cardFeeAmount = (selectedAmount * (gatewayFeeRate ?? 0)) / 100
  const cardTotalCharge = selectedAmount + cardFeeAmount

  return (
    <>
      <RechargeAmountTile
        title="Select Amount"
        selectedAmount={selectedAmount}
        onSelect={onSelectAmount}
        rechargeChoicesData={{ choicesList: choices, currency }}
        disabled={isPending}
      />

      {methodPicker && (
        // Saved cards hang inside the Card tile rather than in a separate "Cards"
        // block below the modes — see RazorpayMethodPicker.
        <RazorpayMethodPicker
          method={method}
          onSelect={methodPicker.onSelectMethod}
          showNetbanking={methodPicker.showNetbanking}
          showBankTransfer={methodPicker.showBankTransfer}
          cardFeeLabel={cardFeeLabel}
          netbankingDisabledReason={methodPicker.netbankingDisabledReason}
          bankTransferDisabledReason={methodPicker.bankTransferDisabledReason}
          disabled={isPending}
          activeCards={activeCards}
          selectedCard={selectedCardId}
          onCardChange={onCardChange}
          onAddCard={onAddCard}
        />
      )}

      {isAddCardIntent && (
        <p className="text-s-400 text-muted-foreground" data-testid="razorpay-custom-add-card-note">
          A minimum payment is required to add and save a card. The selected amount is credited to
          your balance.
        </p>
      )}

      {showCardFeeBreakup && (
        <p className="text-s-400 text-muted-foreground" data-testid="razorpay-custom-fee-breakup">
          A {gatewayFeeRate}% payment gateway fee ({formatCurrency(cardFeeAmount, currency, false)})
          applies. You&apos;ll be charged {formatCurrency(cardTotalCharge, currency, false)}.
        </p>
      )}
      {/* Fee line only when the picker isn't already showing it as a badge on the
          Card tile — otherwise the same "Fee: 2%" appears twice. */}
      {isCard && cardFeeLabel && !showCardFeeBreakup && !showMethodPicker && (
        <p className="text-s-400 text-muted-foreground">{cardFeeLabel}</p>
      )}

      {/* Only when there is no picker to hang the list under (a restricted entry
          point, or an account offered card alone) does the card block stand on its
          own outline. With the picker up, the cards live inside the Card tile. */}
      {showSavedCards && !showMethodPicker && (
        <CardSelector
          title="Cards"
          activeCards={activeCards}
          selectedCard={selectedCardId}
          onCardChange={onCardChange}
          onAddCard={onAddCard}
          isPending={isPending}
          showAddCardButton
        />
      )}

      {/* No saved cards at all: the selector renders its own "Add card" button,
          so nothing extra is needed here. Card method only — netbanking and bank
          transfer have no card to select or add. */}
      {isCard &&
        !showMethodPicker &&
        !showSavedCards &&
        !isAddCardIntent &&
        activeCards.length === 0 && (
          <CardSelector
            title="Cards"
            activeCards={[]}
            selectedCard={0}
            onCardChange={onCardChange}
            onAddCard={onAddCard}
            isPending={isPending}
          />
        )}
    </>
  )
}
